<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("kol.menu.reports") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>

      <!-- Top summary -->
      <div class="row">
        <div class="col-xl-3 col-md-6">
          <stats-card
            :title="$t('kol.total_assignments')"
            type="gradient-primary"
            :sub-title="summary.total.toLocaleString()"
            icon="fa fa-tasks"
          ></stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            :title="$t('kol.total_payment')"
            type="gradient-info"
            :sub-title="formatCurrency(summary.total_payment)"
            icon="fa fa-money-bill-wave"
          ></stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            :title="$t('kol.payment.paid')"
            type="gradient-success"
            :sub-title="summary.paid.toLocaleString()"
            icon="fa fa-check-circle"
          ></stats-card>
        </div>
        <div class="col-xl-3 col-md-6">
          <stats-card
            :title="$t('kol.payment.unpaid')"
            type="gradient-warning"
            :sub-title="summary.unpaid.toLocaleString()"
            icon="fa fa-hourglass-half"
          ></stats-card>
        </div>
      </div>

      <!-- Per-status summary -->
      <div class="row">
        <div class="col-xl col-md-4 col-6" v-for="s in statusList" :key="s.key">
          <stats-card
            :title="$t('kol.status.' + s.key)"
            :type="s.type"
            :sub-title="(summary[s.key] || 0).toLocaleString()"
            :icon="s.icon"
          ></stats-card>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- Report Table -->
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row align-items-center">
            <div class="col-md-3">
              <base-input>
                <el-date-picker
                  v-model="filterDateRange"
                  type="daterange"
                  range-separator="-"
                  :start-placeholder="$t('account.date_from')"
                  :end-placeholder="$t('account.date_to')"
                  format="yyyy-MM-dd"
                  value-format="yyyy-MM-dd"
                >
                </el-date-picker>
              </base-input>
            </div>
            <div class="col-auto">
              <base-button size="md" type="primary" @click="handleSearch()">
                <i class="fa fa-search"></i> {{ $t("search") }}
              </base-button>
              <base-button size="md" type="success" class="ml-2" @click="exportExcel()" :disabled="!tableData || tableData.length === 0">
                <i class="fa fa-file-excel"></i> {{ $t("kol.export_excel") || "Export Excel" }}
              </base-button>
            </div>
            <div class="col text-right">
              <small class="text-muted">{{ totalText }}</small>
            </div>
          </div>
        </template>

        <div>
          <el-table
            :data="tableData"
            row-key="staff_id"
            header-row-class-name="thead-light kol-report-header"
            v-loading="loading"
          >
            <el-table-column :label="$t('kol.staff_name')" prop="staff_name" min-width="180" align="center" header-align="center" sortable>
              <div slot-scope="{ row }">
                {{ row.staff_name || '-' }}
              </div>
            </el-table-column>

            <el-table-column :label="$t('kol.total_assignments')" prop="total_count" min-width="120" align="center" header-align="center" sortable>
              <template slot-scope="{ row }">
                <a href="javascript:void(0)" class="font-weight-bold" @click="goAssignments(row)">
                  {{ (row.total_count || 0).toLocaleString() }}
                </a>
              </template>
            </el-table-column>

            <el-table-column v-for="col in statusColumns" :key="col.key"
              :label="$t('kol.status.' + col.key)" :prop="col.prop" min-width="120" align="center" header-align="center" sortable>
              <template slot-scope="{ row }">
                <a href="javascript:void(0)" :class="'text-' + col.type" @click="goAssignments(row, { status: col.key })">
                  {{ (row[col.prop] || 0).toLocaleString() }}
                </a>
              </template>
            </el-table-column>

            <el-table-column :label="$t('kol.payment.paid')" prop="count_paid" min-width="110" align="center" header-align="center" sortable>
              <template slot-scope="{ row }">
                <a href="javascript:void(0)" class="text-success" @click="goAssignments(row, { payment_status: 'paid' })">
                  {{ (row.count_paid || 0).toLocaleString() }}
                </a>
              </template>
            </el-table-column>

            <el-table-column :label="$t('kol.payment.unpaid')" prop="count_unpaid" min-width="110" align="center" header-align="center" sortable>
              <template slot-scope="{ row }">
                <a href="javascript:void(0)" class="text-warning" @click="goAssignments(row, { payment_status: 'unpaid' })">
                  {{ (row.count_unpaid || 0).toLocaleString() }}
                </a>
              </template>
            </el-table-column>

            <el-table-column :label="$t('kol.total_payment')" prop="total_payment" min-width="150" align="center" header-align="center" sortable>
              <div slot-scope="{ row }">
                {{ formatCurrency(row.total_payment) }}
              </div>
            </el-table-column>
          </el-table>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, DatePicker } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import StatsCard from "@/components/Cards/StatsCard";
import { mapGetters, mapActions } from "vuex";
import * as XLSX from "xlsx";

export default {
  components: {
    RouteBreadCrumb,
    StatsCard,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [DatePicker.name]: DatePicker,
  },
  computed: {
    ...mapGetters({
      tableData: "KOL/getReportByStaff",
    }),
    summary() {
      const s = {
        total: 0,
        total_payment: 0,
        pending: 0,
        briefed: 0,
        product_sent: 0,
        posted: 0,
        completed: 0,
        paid: 0,
        unpaid: 0,
      };
      (this.tableData || []).forEach(r => {
        s.total += Number(r.total_count) || 0;
        s.total_payment += Number(r.total_payment) || 0;
        s.pending += Number(r.count_pending) || 0;
        s.briefed += Number(r.count_briefed) || 0;
        s.product_sent += Number(r.count_product_sent) || 0;
        s.posted += Number(r.count_posted) || 0;
        s.completed += Number(r.count_completed) || 0;
        s.paid += Number(r.count_paid) || 0;
        s.unpaid += Number(r.count_unpaid) || 0;
      });
      return s;
    },
    totalText() {
      const n = (this.tableData || []).length;
      return `${n} ${this.$t("kol.staff")}`;
    },
  },
  data() {
    return {
      loading: false,
      filterDateRange: null,
      statusList: [
        { key: "pending",      type: "gradient-info",    icon: "fa fa-clock" },
        { key: "briefed",      type: "gradient-primary", icon: "fa fa-file-alt" },
        { key: "product_sent", type: "gradient-warning", icon: "fa fa-box" },
        { key: "posted",       type: "gradient-success", icon: "fa fa-share-square" },
        { key: "completed",    type: "gradient-default", icon: "fa fa-flag-checkered" },
      ],
      statusColumns: [
        { key: "pending",      prop: "count_pending",      type: "info" },
        { key: "briefed",      prop: "count_briefed",      type: "primary" },
        { key: "product_sent", prop: "count_product_sent", type: "warning" },
        { key: "posted",       prop: "count_posted",       type: "success" },
        { key: "completed",    prop: "count_completed",    type: "default" },
      ],
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    ...mapActions({
      fetchReportByStaff: "KOL/fetchReportByStaff",
    }),
    async loadData() {
      this.loading = true;
      try {
        const params = {};
        if (this.filterDateRange && this.filterDateRange.length === 2) {
          params.brief_date_from = this.filterDateRange[0];
          params.brief_date_to = this.filterDateRange[1];
        }
        await this.fetchReportByStaff(params);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      this.loadData();
    },
    formatCurrency(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("th-TH", {
        style: "currency",
        currency: "THB",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    exportExcel() {
      if (!this.tableData || this.tableData.length === 0) return;
      const headers = [
        this.$t("kol.staff_name"),
        this.$t("kol.total_assignments"),
        this.$t("kol.status.pending"),
        this.$t("kol.status.briefed"),
        this.$t("kol.status.product_sent"),
        this.$t("kol.status.posted"),
        this.$t("kol.status.completed"),
        this.$t("kol.payment.paid"),
        this.$t("kol.payment.unpaid"),
        this.$t("kol.total_payment"),
      ];
      const rows = this.tableData.map(r => [
        r.staff_name || "-",
        Number(r.total_count) || 0,
        Number(r.count_pending) || 0,
        Number(r.count_briefed) || 0,
        Number(r.count_product_sent) || 0,
        Number(r.count_posted) || 0,
        Number(r.count_completed) || 0,
        Number(r.count_paid) || 0,
        Number(r.count_unpaid) || 0,
        Number(r.total_payment) || 0,
      ]);
      // Totals row
      rows.push([
        this.$t("total"),
        this.summary.total,
        this.summary.pending,
        this.summary.briefed,
        this.summary.product_sent,
        this.summary.posted,
        this.summary.completed,
        this.summary.paid,
        this.summary.unpaid,
        this.summary.total_payment,
      ]);

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "KOL Report");
      const suffix =
        this.filterDateRange && this.filterDateRange.length === 2
          ? `_${this.filterDateRange[0]}_${this.filterDateRange[1]}`
          : `_${new Date().toISOString().slice(0, 10)}`;
      XLSX.writeFile(wb, `kol_report${suffix}.xlsx`);
    },
    goAssignments(row, extra = {}) {
      const query = { staff_id: row.staff_id };
      if (this.filterDateRange && this.filterDateRange.length === 2) {
        query.brief_date_from = this.filterDateRange[0];
        query.brief_date_to = this.filterDateRange[1];
      }
      Object.assign(query, extra);
      this.$router.push({ path: "/kol/assignments", query });
    },
  },
};
</script>

<style>
.no-border-card .card-footer {
  border-top: 0;
}
.badge-lg {
  font-size: 0.8rem;
  padding: 0.5em 0.8em;
}
.kol-report-header th,
.kol-report-header th .cell {
  text-align: center !important;
  justify-content: center !important;
}
</style>
